import React, { useMemo } from "react";
import { motion } from "framer-motion";

const BRANDS = [
  { id: "apple", name: "iPhone", initials: "iP", tag: "Apple", accent: "from-slate-900 to-slate-600" },
  { id: "samsung", name: "Samsung", initials: "S", tag: "Galaxy", accent: "from-blue-800 to-blue-500" },
  { id: "oneplus", name: "OnePlus", initials: "1+", tag: "Never Settle", accent: "from-red-700 to-red-500" },
  { id: "xiaomi", name: "Xiaomi", initials: "Mi", tag: "Redmi · Poco", accent: "from-orange-600 to-amber-500" },
  { id: "vivo", name: "Vivo · iQOO", initials: "V", tag: "V · X · iQOO", accent: "from-indigo-700 to-blue-400" },
  { id: "oppo", name: "Oppo", initials: "O", tag: "Reno · Find", accent: "from-green-700 to-emerald-500" },
  { id: "realme", name: "Realme", initials: "r", tag: "GT · Narzo", accent: "from-yellow-500 to-orange-500" },
  { id: "google", name: "Google Pixel", initials: "G", tag: "Pixel 9 · 8", accent: "from-teal-600 to-sky-500" },
  { id: "motorola", name: "Motorola", initials: "M", tag: "Edge · Razr", accent: "from-purple-700 to-fuchsia-500" },
  { id: "nothing", name: "Nothing", initials: "N", tag: "Phone (1/2/3)", accent: "from-zinc-800 to-zinc-500" },
  { id: "honor", name: "Honor", initials: "H", tag: "Magic · X", accent: "from-rose-700 to-pink-500" },
  { id: "asus", name: "Asus ROG", initials: "R", tag: "ROG · Zenfone", accent: "from-red-900 to-red-500" },
  { id: "sony", name: "Sony Xperia", initials: "Xp", tag: "Xperia 1 · 5", accent: "from-slate-800 to-slate-500" },
  { id: "tecno", name: "Tecno", initials: "T", tag: "Camon · Phantom", accent: "from-cyan-700 to-cyan-400" },
  { id: "infinix", name: "Infinix", initials: "iF", tag: "Zero · Note", accent: "from-lime-700 to-lime-400" },
  { id: "nokia", name: "Nokia", initials: "N", tag: "HMD", accent: "from-blue-900 to-blue-500" },
];

export default function BrandSelector({ selected, onSelect, availableBrands }) {
  // Only show brands that have at least one product available
  const list = useMemo(() => {
    if (!availableBrands || availableBrands.length === 0) return BRANDS;
    const set = new Set(availableBrands);
    return BRANDS.filter((b) => set.has(b.id));
  }, [availableBrands]);

  return (
    <section id="brands" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 relative">
      <div className="absolute inset-0 tv-dot-bg opacity-[0.06] pointer-events-none" />
      <div className="relative flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <span className="tv-chip mb-3">01 · Pick Brand</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter">
            Which phone are you selling?
          </h2>
          <p className="text-muted-foreground mt-2 text-base sm:text-lg">
            {list.length}+ brands supported — instant quote in 45 seconds.
          </p>
        </div>
      </div>

      <div className="relative grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
        {list.map((b) => {
          const active = selected === b.id;
          return (
            <motion.button
              key={b.id}
              whileHover={{ y: -3, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onSelect(b.id)}
              data-testid={`brand-card-${b.id}`}
              className={`relative overflow-hidden rounded-2xl p-4 text-left border-2 transition-all ${
                active
                  ? "border-primary shadow-xl shadow-primary/25 bg-primary/5 -translate-y-1"
                  : "border-border bg-card hover:border-primary/40"
              }`}
            >
              <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full bg-gradient-to-br ${b.accent} opacity-10 group-hover:opacity-20 transition`} />
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${b.accent} text-white flex items-center justify-center mb-3 font-brand font-extrabold text-lg shadow-lg`}>
                {b.initials}
              </div>
              <div className="font-bold text-sm sm:text-base leading-tight">{b.name}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{b.tag}</div>
              <div className={`mt-3 text-[10px] font-mono uppercase tracking-widest font-bold ${active ? "text-primary" : "text-muted-foreground"}`}>
                {active ? "✓ Selected" : "Tap to select"}
              </div>
            </motion.button>
          );
        })}
      </div>
    </section>
  );
}
